import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { removeQuestion, restoreQuestion, type Question } from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { type } from '@/theme/typography';

interface Props {
  /** The set that owns this question — the address of both routes, and the write gate. */
  setId: number;
  /** The row as it stands. Its `is_active` decides which of the two actions this dialog is. */
  question: Question;
  /**
   * Called when the act has been **accepted**, before this closes: the row it was about, and whether it
   * is now active. It is the table's, not this dialog's, because both things owed at that moment
   * outlive this component — the announcement has to be written into a live region that stays mounted,
   * and the focus move has to wait for the refetch that takes the row off screen (a `ui-reviewer`
   * live-pass finding: focus was landing on `<body>` and nothing was announced).
   */
  onCompleted?: (question: Question, nowActive: boolean) => void;
  onClose: () => void;
}

/**
 * §6.9's **Remove** — and its **Restore**, which is the same shape with the other endpoint.
 *
 * ⚠️ **This is one dialog where there were two, and the merge is the sprint's own decision rather
 * than a tidy-up** (2026-09-24, ADR 0019 §5, superseding ADR 0018 decision 5). `ArchiveDialog` asked
 * *"Archive this question?"* about a library entry — a wording **retracted** with the library — and
 * `RemoveFromSetDialog` asked *"Remove this question from the set?"* about a link. With **one set per question** those are the same act: every
 * removal is a removal from the question's only set, the route is `POST …/questions/{id}/remove/`, and
 * it **archives**. Keeping both would be two controls doing one thing, which is a screen lying about
 * itself. The surviving name is the API's — *Remove* — and *Archive* survives only as the **state** a
 * removed row reads and as the *Show archived* affordance that brings it back.
 *
 * ⚠️ **This is what "delete" means for a question, and it is the whole of feedback protection #2 (ADR
 * 0005).** There is no delete anywhere: `Score.question` is `on_delete=PROTECT`, so destroying a
 * question would destroy the recorded feedback that quotes it, and the API declares **no `DELETE`
 * operation on any path of this surface** for a client to call. The row is never removed — it keeps its
 * id, its wording, its category, its difficulty, **its set and its `position`** — and only `is_active`
 * moves.
 *
 * ⚠️ **What the confirmation says, and all three clauses are the server's rather than a paraphrase:**
 *
 *  1. **It will no longer appear in new interviews.** One `Score` is written per **active** question of
 *     the set an interview is arranged from (`apps/interviews/services.py`, ADR 0004's moment), so the
 *     removal takes effect at that boundary and nowhere else.
 *  2. **An interview already arranged still asks it — a draft nobody has opened included** — and its
 *     score stays writable (`apps/interviews/tests/test_autosave_api.py::
 *     test_a_question_archived_after_the_interview_started_stays_writable`: *"archiving is not a way to
 *     close a conversation somebody is in the middle of"*). A reader told only about *scores already
 *     given* could reasonably conclude that today's untouched draft loses the question.
 *  3. **Nothing is blanked and nothing is taken out.** The wording in every recorded score is the
 *     frozen snapshot, and the row **stays in this set, in its place** — listed again the moment *Show
 *     archived* is on, and restorable from the same menu. ⚠️ **That third clause is true again, and it
 *     was false between 2026-08-11 and 2026-09-24**: it said the row *"keeps its place in the bank"*,
 *     was retracted when a library turned out to have no order of its own, and is restored here because
 *     `position` is back on the question and an archived row keeps it (ADR 0019) — which is precisely
 *     why *Restore* puts a question back where it sat rather than at the end.
 *
 * ⚠️ **Deliberately light, and that is a decision.** No acknowledgement checkbox, no `danger` button,
 * no *"cannot be undone"* — this **can** be undone from the same menu, and ADR 0009's weight belongs to
 * §6.5's erasure, which has no undo anywhere. An over-heavy confirmation on a reversible act trains
 * people to click through the one that matters. `CloseVacancyDialog` took the same decision for the
 * same reason on §6.8. What the dialog *is* for is that this is not a silent toggle: the three
 * sentences above are what a menu item firing the request itself could not say, and a write fired from
 * a menu item would also have nowhere to report a 403, a throttle or a lost connection.
 *
 * ⚠️ **Both requests carry no body at all.** `is_active` is not writable through `PATCH` — neither
 * `QuestionIn` nor `QuestionUpdate` carries the field, and the document declares **no `requestBody`**
 * for either action — so a body appearing here would be the first sign that the archive had become a
 * field edit through the verb (ADR 0005, the shape of ADR 0010). Asserted as `data === undefined` on
 * both.
 *
 * ⚠️ **The write gate is the set's, and this dialog asks nothing** (ADR 0019). It is rendered from the
 * row menu, which the screen renders only for a viewer who may write the **set** — so a predicate here
 * would be the second copy of a rule that has one place. §5 decides the rest: hidden, never disabled.
 */
export function RemoveQuestionDialog({ setId, question, onCompleted, onClose }: Props) {
  const { t } = useTranslation(['questions', 'common', 'components']);
  const queryClient = useQueryClient();

  /**
   * Which direction this is. The row as it stands decides, and the menu offers only the other one — so
   * *Remove* and *Restore* are never both on offer and neither is a state the reader has to work out.
   */
  const removing = question.is_active;

  const write = useMutation<Question, ApiError, void>({
    /**
     * ⚠️ **No body, and not even an empty object.** Both are actions rather than updates; `api/http.ts`
     * is handed `undefined` and axios sends nothing (`api/vacancies.ts`).
     */
    mutationFn: () =>
      removing ? removeQuestion(setId, question.id) : restoreQuestion(setId, question.id),
    /**
     * ⚠️ **Both families.** `queryKeyRoots.questionSets` is this set — its composition and its
     * `active_question_count`, which is a column of the list row as well as of the detail — and
     * `queryKeyRoots.roles` is `RoleOut.active_question_count`, which counts the distinct active
     * questions of a vacancy's active sets, so this write moves §6.8's cell for every vacancy the set
     * is attached to. The client cannot tell from any payload whether this set is attached anywhere,
     * and asking the catalogue is cheaper than leaving it wrong.
     *
     * Nothing is written into the cache by hand: the refetch is what confirms the flag moved, and the
     * order is the API's.
     *
     * ⚠️ **`onCompleted` before `onClose`**, and it is told the state the row is **going to** be in —
     * the server has agreed by now, so `!removing` is a fact rather than a guess. It is called here and
     * not on the answer's own `is_active` for the reason the idempotency criterion records: the two are
     * the same in every case that matters, and comparing them is how this screen would grow a special
     * case for a removal that changed nothing.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onCompleted?.(question, !removing);
      onClose();
    },
    /**
     * ⚠️ **A 404 is answered by re-reading and never as a failure line in here.** A question in another
     * tenant — or in a set outside this reader's reach — is indistinguishable from one that never
     * existed, and there is nothing in this dialog a reader could correct. Both families are asked
     * again — the re-read is what takes the row off screen — and the dialog closes.
     */
    onError: (error) => {
      if (!error.isNotFound) return;
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onClose();
    },
  });

  /** §8 in a dialog: what failed, in words, never a status code and never the server's `detail`. */
  const failure = (() => {
    if (!write.error || write.error.isNotFound) return null;
    if (write.error.isOffline) return t('questions:dialog.offline');
    if (write.error.isThrottled) return t('components:state.throttled');
    return t(removing ? 'questions:remove.failed' : 'questions:restore.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Esc and the overlay cancel, and cancelling writes nothing. The amended `Dialog` (TASK-069)
        // owns the focus move and the way back to the opener; nothing here defeats it or re-fixes it.
        if (!next) onClose();
      }}
      /**
       * ⚠️ **No room is left to interpolate the row into the title**, so the question being acted on is
       * named in the body instead (below) — which the failure mode of a row action makes non-optional:
       * doing it to the wrong row.
       */
      title={t(removing ? 'questions:remove.title' : 'questions:restore.title')}
      description={t(removing ? 'questions:remove.description' : 'questions:restore.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/*
           * ⚠️ **The ordinary primary button, not `danger`.** `danger` is outlined in `negative` and §4
           * reserves that for what cannot be taken back; wearing it here would make a reversible act
           * look like §6.5's erasure and teach the reader that the two are the same weight. It is not
           * autofocused either — `Dialog` puts focus on the content (§9) — so neither action is one
           * Enter away.
           */}
          <Button onClick={() => write.mutate()} isLoading={write.isPending}>
            {t(removing ? 'questions:remove.submit' : 'questions:restore.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-900">
        {/* Which row this is, because a set has one of these per question and the wording is the only
            thing that tells them apart. The label is the table's own column name. */}
        <div>
          <p className={type.eyebrow}>{t('questions:table.columns.question')}</p>
          <p className="mt-0.5 [overflow-wrap:anywhere]">{question.text}</p>
        </div>

        {/* What the act does, first, because it is what the reader is actually asking about. */}
        <p className="font-medium">
          {t(removing ? 'questions:remove.kept' : 'questions:restore.kept')}
        </p>

        {/* The half that sentence leaves out: the snapshot is taken when the interview is arranged, so
            an interview that already exists is unaffected in either direction. */}
        <p>{t(removing ? 'questions:remove.arranged' : 'questions:restore.arranged')}</p>

        {/* It stays in this set, in its place, and this is reversible from the same menu. */}
        <p className="text-ink-700">
          {t(removing ? 'questions:remove.place' : 'questions:restore.place')}
        </p>

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
